export default {
    /**
     * Do async things specified in a callback to a given array of elements
     * Idea from: https://codeburst.io/javascript-async-await-with-foreach-b6ba62bbf404
     */
    async asyncForEach( array, callback ) {
        // eslint-disable-next-line no-plusplus
        for ( let index = 0; index < array.length; index++ ) {
            const isLast = index === ( array.length - 1 );
            // eslint-disable-next-line no-await-in-loop
            await callback( array[ index ], isLast, index, array );
        }
    },

    /**
     * Recursively find the changes between a reference and possibly modified value
     * Returns the modified value if there is a change, otherwise return null
     * @param { String || Number || Object || Array } reference
     * @param { String || Number || Object || Array } modified
     * @returns { String || Number || Object || Array || Null }
     */
    changes( reference, modified ) {
        if ( ( reference instanceof Array ) && ( modified instanceof Array ) ) {
            // stringify & parse so we don't inadvertently sort 'reference' or 'modified' arrays outside of this function
            const modifiedSorted = JSON.parse( JSON.stringify( modified ) )
                .sort();
            const changes = JSON.parse( JSON.stringify( reference ) )
                .sort()
                .map( ( item, index ) => this.changes( item, modifiedSorted[ index ] ) )
                .filter( ( item ) => item !== null );
            return changes.length === 0
                ? null
                : changes;
        }
        if ( ( reference instanceof Object ) && ( modified instanceof Object ) ) {
            const referenceKeys = Object.keys( reference );
            const changes = {};
            referenceKeys.forEach( ( key ) => {
                const value = this.changes( reference[ key ], modified[ key ] );
                if ( value !== null ) {
                    changes[ key ] = value;
                }
            } );
            return Object.keys( changes ).length === 0
                ? null
                : changes;
        }
        return reference === modified
            ? null
            : modified;
    },

    /**
     * @public
     * Key an Object from a given Array by a given attribute
     * @param { Object[] } array - An array of Objects
     * @param { String | Object } key - A string, or an object containing an array of 'keys'
     * key as an object : {
     *     keys: [ { String }, { String } ],
     *     delimiter: '|',
     * }
     * Note: Make sure the values for your keys across your dataset are sufficiently unique
     * If you have fewer entries coming out of normalize() than you did going in, your dataset has multiple entries with the same key values
     * @returns { Object }
     */
    normalize( array, key ) {
        const tmpObj = {};
        array.forEach( ( item ) => {
            // Bail out if key is not provided
            if ( !key ) return item;
            // If we have a key that is a string, and that string is an attribute on the item
            if ( ( typeof key === 'string' ) && ( key in item ) ) {
                // Key by the given key
                tmpObj[ item[ key ] ] = item;
            } else if ( key.keys
                && key.keys.length
                && ( key.keys.length > 0 )
                && key.keys.every( ( currentKey ) => currentKey in item )
            ) {
                const delimiter = key.delimiter || '|';
                // Use reduce to concatenate the keys and the given delimiter
                tmpObj[ key.keys.reduce( ( accumulator, currentItem, index ) => (
                    index === 0
                        ? `${ accumulator }${ item[ currentItem ] }`
                        : `${ accumulator }${ delimiter }${ item[ currentItem ] }`
                ), '' ) ] = item;
            }
            // Return the item if the key is not a String or an Object
            return item;
        } );
        return tmpObj;
    },

    /**
     * Creates a base64 encoded string for authentication.
     *
     * @params `username` - The username of the user.
     * @params `password` - The password of the user.
     * @returns A base64 encoded string for authentication.
     */
    getBase64AuthString(username, password) {
        if (!username) throw new Error('No username was provided');
        if (!password) throw new Error('No password was provided');

        return `Basic ${btoa(`${username}:${password}`)}`;
    },
};
